import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import {
  AttachmentResponseDto,
  BautagebuchDataspaceImportPreviewDto,
  BautagebuchDataspaceImportRequestDto,
  DataOfferCatalogDetailDto,
  DataOfferCatalogItemDto,
  DataOfferResponseDto,
  DataOfferUpsertRequestDto,
  DataspaceAssetCreateRequestDto,
  DataspaceAssetDetailDto,
  DataspaceAssetResponseDto,
  DataspaceAccessRequestCreateDto,
  DataspaceAccessRequestDecisionDto,
  DataspaceAccessRequestDto,
  DataspaceAgreementDto,
  DataspaceCatalogItemDto,
  DataspaceDemoSeedResponseDto,
  DataspaceExportResponseDto,
  DataspacePolicyDto,
  DataspacePolicyTemplateDto,
  DataspaceProjectOverviewDto,
  DataspacePullRequestDto,
  DataspacePullResponseDto,
  DataspaceRuntimeCleanupResponseDto,
  NuReleaseDispatchRequestDto,
  NuReleaseDispatchResponseDto,
  Page,
  Pageable,
  SubmissionResponseDto,
  SubmissionStatus,
} from '../models/bautagebuch.models';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class BautagebuchService {
  private bautagebuchSubmissionsBaseUrl = '/proxy/api/v0/bautagebuchservice/api/v1/submissions';
  private dataspaceBaseUrl = '/proxy/api/v0/bautagebuchservice/api/v1/dataspace';

  constructor(private http: HttpClient) { }

  private getAuthHeaders(): HttpHeaders {
    const token = this.getAuthToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
  }

  private getOrgHeaders(organisationId: number): HttpHeaders {
    return this.getAuthHeaders().set('X-Organisation-Id', String(organisationId));
  }

  private getAuthToken(): string {
    return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token') || '';
  }

  // ==================== Bautagebuch Submission Methods ====================

  listBautagebuchSubmissions(
    organisationId: number,
    pageable?: Pageable,
    status?: SubmissionStatus
  ): Observable<Page<SubmissionResponseDto>> {
    const headers = this.getOrgHeaders(organisationId);
    let params = new HttpParams();

    if (pageable) {
      if (pageable.page !== undefined) params = params.set('page', String(pageable.page));
      if (pageable.size !== undefined) params = params.set('size', String(pageable.size));
      if (pageable.sort) params = params.set('sort', pageable.sort);
    }

    if (status !== undefined) {
      params = params.set('status', status);
    }

    const req$ = this.http.get<Page<SubmissionResponseDto>>(this.bautagebuchSubmissionsBaseUrl, {
      headers,
      params
    });
    return this.handleRequestErrors(req$);
  }

  archiveBautagebuchSubmission(id: string, organisationId: number): Observable<SubmissionResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<SubmissionResponseDto>(
      `${this.bautagebuchSubmissionsBaseUrl}/${id}/archive`,
      {},
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  deleteBautagebuchDraft(id: string, organisationId: number): Observable<void> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.delete<void>(`${this.bautagebuchSubmissionsBaseUrl}/${id}`, { headers });
    return this.handleRequestErrors(req$);
  }

  getBautagebuchSubmission(id: string, organisationId: number): Observable<SubmissionResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.get<SubmissionResponseDto>(`${this.bautagebuchSubmissionsBaseUrl}/${id}`, { headers });
    return this.handleRequestErrors(req$);
  }

  getBautagebuchEnrichedXml(submissionId: string, organisationId: number): Observable<string> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.get(`${this.bautagebuchSubmissionsBaseUrl}/${submissionId}/xml`, {
      headers,
      responseType: 'text'
    });
    return this.handleRequestErrors(req$);
  }

  // ==================== Bautagebuch Attachment Methods ====================

  uploadBautagebuchAttachment(
    submissionId: string,
    organisationId: number,
    file: File
  ): Observable<AttachmentResponseDto> {
    const token = this.getAuthToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'X-Organisation-Id': String(organisationId)
    });

    const formData = new FormData();
    formData.append('file', file);

    const req$ = this.http.post<AttachmentResponseDto>(
      `${this.bautagebuchSubmissionsBaseUrl}/${submissionId}/attachments`,
      formData,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  listBautagebuchAttachments(
    submissionId: string,
    organisationId: number
  ): Observable<AttachmentResponseDto[]> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.get<AttachmentResponseDto[]>(
      `${this.bautagebuchSubmissionsBaseUrl}/${submissionId}/attachments`,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  downloadBautagebuchAttachment(
    submissionId: string,
    organisationId: number,
    attachmentId: string
  ): Observable<Blob> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.get(
      `${this.bautagebuchSubmissionsBaseUrl}/${submissionId}/attachments/${attachmentId}/download`,
      { headers, responseType: 'blob' }
    );
    return this.handleRequestErrors(req$);
  }

  deleteBautagebuchAttachment(
    submissionId: string,
    organisationId: number,
    attachmentId: string
  ): Observable<void> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.delete<void>(
      `${this.bautagebuchSubmissionsBaseUrl}/${submissionId}/attachments/${attachmentId}`,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  updateBautagebuchAttachmentAnlageart(
    submissionId: string,
    organisationId: number,
    attachmentId: string,
    anlageartCode: string
  ): Observable<AttachmentResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const body = { anlageartCode };
    const req$ = this.http.patch<AttachmentResponseDto>(
      `${this.bautagebuchSubmissionsBaseUrl}/${submissionId}/attachments/${attachmentId}`,
      body,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  // ==================== Dataspace Methods ====================

  createDataOffer(
    organisationId: number,
    dto: DataOfferUpsertRequestDto
  ): Observable<DataOfferResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<DataOfferResponseDto>(
      `${this.dataspaceBaseUrl}/offers`,
      dto,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  updateDataOffer(
    organisationId: number,
    offerId: string,
    dto: DataOfferUpsertRequestDto
  ): Observable<DataOfferResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.put<DataOfferResponseDto>(
      `${this.dataspaceBaseUrl}/offers/${offerId}`,
      dto,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  getMyDataOffers(organisationId: number): Observable<DataOfferResponseDto[]> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.get<DataOfferResponseDto[]>(
      `${this.dataspaceBaseUrl}/offers/mine`,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  getDataOffer(organisationId: number, offerId: string): Observable<DataOfferResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.get<DataOfferResponseDto>(
      `${this.dataspaceBaseUrl}/offers/${offerId}`,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  getDataOfferCatalog(
    organisationId: number,
    options?: { projectKey?: string }
  ): Observable<DataOfferCatalogItemDto[]> {
    const headers = this.getOrgHeaders(organisationId);
    let params = new HttpParams();
    if (options?.projectKey) params = params.set('projectKey', options.projectKey);
    const req$ = this.http.get<DataOfferCatalogItemDto[]>(
      `${this.dataspaceBaseUrl}/offers/catalog`,
      { headers, params }
    );
    return this.handleRequestErrors(req$);
  }

  getDataOfferCatalogDetail(
    organisationId: number,
    offerId: string
  ): Observable<DataOfferCatalogDetailDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.get<DataOfferCatalogDetailDto>(
      `${this.dataspaceBaseUrl}/offers/catalog/${offerId}`,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  confirmDataOfferFakePayment(
    organisationId: number,
    offerId: string
  ): Observable<DataOfferCatalogDetailDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<DataOfferCatalogDetailDto>(
      `${this.dataspaceBaseUrl}/offers/catalog/${offerId}/fake-payment`,
      {},
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  exportDataOfferCatalog(
    organisationId: number,
    offerId: string,
    format: 'PDF' | 'XML'
  ): Observable<DataspaceExportResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const params = new HttpParams().set('format', format);
    const req$ = this.http.post<DataspaceExportResponseDto>(
      `${this.dataspaceBaseUrl}/offers/catalog/${offerId}/export`,
      {},
      { headers, params }
    );
    return this.handleRequestErrors(req$);
  }

  publishDataOffer(organisationId: number, offerId: string): Observable<DataOfferResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<DataOfferResponseDto>(
      `${this.dataspaceBaseUrl}/offers/${offerId}/publish`,
      {},
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  revokeDataOffer(organisationId: number, offerId: string): Observable<DataOfferResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<DataOfferResponseDto>(
      `${this.dataspaceBaseUrl}/offers/${offerId}/revoke`,
      {},
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  invalidateDataOffer(organisationId: number, offerId: string): Observable<DataOfferResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<DataOfferResponseDto>(
      `${this.dataspaceBaseUrl}/offers/${offerId}/invalidate`,
      {},
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  archiveDataOffer(organisationId: number, offerId: string): Observable<DataOfferResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<DataOfferResponseDto>(
      `${this.dataspaceBaseUrl}/offers/${offerId}/archive`,
      {},
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  deleteDataOffer(
    organisationId: number,
    offerId: string
  ): Observable<void> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.delete<void>(
      `${this.dataspaceBaseUrl}/offers/${offerId}`,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  publishDataspaceAsset(
    organisationId: number,
    dto: DataspaceAssetCreateRequestDto
  ): Observable<DataspaceAssetResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<DataspaceAssetResponseDto>(
      `${this.dataspaceBaseUrl}/assets`,
      dto,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  dispatchNuRelease(
    organisationId: number,
    dto: NuReleaseDispatchRequestDto
  ): Observable<NuReleaseDispatchResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<NuReleaseDispatchResponseDto>(
      `${this.dataspaceBaseUrl}/nu-releases/dispatch`,
      dto,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  updateDataspaceAsset(
    organisationId: number,
    assetId: string,
    dto: DataspaceAssetCreateRequestDto
  ): Observable<DataspaceAssetResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.put<DataspaceAssetResponseDto>(
      `${this.dataspaceBaseUrl}/assets/${assetId}`,
      dto,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  getMyDataspaceAssets(organisationId: number): Observable<DataspaceAssetResponseDto[]> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.get<DataspaceAssetResponseDto[]>(
      `${this.dataspaceBaseUrl}/assets/mine`,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  getMyDataspacePolicies(organisationId: number): Observable<DataspacePolicyDto[]> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.get<DataspacePolicyDto[]>(
      `${this.dataspaceBaseUrl}/policies/mine`,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  getDataspacePolicyTemplates(): Observable<DataspacePolicyTemplateDto[]> {
    const headers = this.getAuthHeaders();
    const req$ = this.http.get<DataspacePolicyTemplateDto[]>(
      `${this.dataspaceBaseUrl}/policies/templates`,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  revokeDataspaceAsset(
    organisationId: number,
    assetId: string
  ): Observable<DataspaceAssetResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<DataspaceAssetResponseDto>(
      `${this.dataspaceBaseUrl}/assets/${assetId}/revoke`,
      {},
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  deleteDataspaceAsset(
    organisationId: number,
    assetId: string
  ): Observable<void> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.delete<void>(
      `${this.dataspaceBaseUrl}/assets/${assetId}`,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  getDataspaceCatalog(
    organisationId: number,
    options?: { projectKey?: string; reportDate?: string; purpose?: string }
  ): Observable<DataspaceCatalogItemDto[]> {
    const headers = this.getOrgHeaders(organisationId);
    let params = new HttpParams();
    if (options?.projectKey) params = params.set('projectKey', options.projectKey);
    if (options?.reportDate) params = params.set('reportDate', options.reportDate);
    if (options?.purpose) params = params.set('purpose', options.purpose);
    const req$ = this.http.get<DataspaceCatalogItemDto[]>(
      `${this.dataspaceBaseUrl}/catalog`,
      { headers, params }
    );
    return this.handleRequestErrors(req$);
  }

  pullDataspaceAsset(
    organisationId: number,
    assetId: string,
    dto: DataspacePullRequestDto
  ): Observable<DataspacePullResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<DataspacePullResponseDto>(
      `${this.dataspaceBaseUrl}/catalog/${assetId}/pull`,
      dto,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  getDataspaceAssetDetail(
    organisationId: number,
    assetId: string
  ): Observable<DataspaceAssetDetailDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.get<DataspaceAssetDetailDto>(
      `${this.dataspaceBaseUrl}/assets/${assetId}/detail`,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  requestDataspaceAccess(
    organisationId: number,
    assetId: string,
    dto: DataspaceAccessRequestCreateDto
  ): Observable<DataspaceAccessRequestDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<DataspaceAccessRequestDto>(
      `${this.dataspaceBaseUrl}/assets/${assetId}/access-requests`,
      dto,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  decideDataspaceAccessRequest(
    organisationId: number,
    requestId: string,
    dto: DataspaceAccessRequestDecisionDto
  ): Observable<DataspaceAccessRequestDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<DataspaceAccessRequestDto>(
      `${this.dataspaceBaseUrl}/access-requests/${requestId}/decision`,
      dto,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  acknowledgeDataspaceAsset(
    organisationId: number,
    assetId: string
  ): Observable<DataspaceAssetDetailDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<DataspaceAssetDetailDto>(
      `${this.dataspaceBaseUrl}/assets/${assetId}/acknowledge`,
      {},
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  exportDataspaceAsset(
    organisationId: number,
    assetId: string,
    dto: DataspacePullRequestDto
  ): Observable<DataspaceExportResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<DataspaceExportResponseDto>(
      `${this.dataspaceBaseUrl}/assets/${assetId}/export`,
      dto,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  getMyDataspaceAccessRequests(organisationId: number): Observable<DataspaceAccessRequestDto[]> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.get<DataspaceAccessRequestDto[]>(
      `${this.dataspaceBaseUrl}/access-requests/mine`,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  getMyDataspaceAgreements(organisationId: number): Observable<DataspaceAgreementDto[]> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.get<DataspaceAgreementDto[]>(
      `${this.dataspaceBaseUrl}/agreements`,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  getDataspaceProjectOverview(organisationId: number): Observable<DataspaceProjectOverviewDto[]> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.get<DataspaceProjectOverviewDto[]>(
      `${this.dataspaceBaseUrl}/projects/overview`,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  seedDataspaceDemo(organisationId: number): Observable<DataspaceDemoSeedResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<DataspaceDemoSeedResponseDto>(
      `${this.dataspaceBaseUrl}/demo-seed`,
      {},
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  cleanupDataspaceRuntime(organisationId: number): Observable<DataspaceRuntimeCleanupResponseDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<DataspaceRuntimeCleanupResponseDto>(
      `${this.dataspaceBaseUrl}/runtime-cleanup`,
      {},
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  importDataspaceAssetsToBautagebuch(
    organisationId: number,
    dto: BautagebuchDataspaceImportRequestDto
  ): Observable<BautagebuchDataspaceImportPreviewDto> {
    const headers = this.getOrgHeaders(organisationId);
    const req$ = this.http.post<BautagebuchDataspaceImportPreviewDto>(
      `${this.dataspaceBaseUrl}/imports/bautagebuch`,
      dto,
      { headers }
    );
    return this.handleRequestErrors(req$);
  }

  // ==================== Error Handling ====================

  handleRequestErrors<T>(source$: Observable<T>): Observable<T> {
    return source$.pipe(
      catchError(err => {
        const message = this.mapErrorToMessage(err);
        return throwError(() => ({ original: err, message }));
      })
    );
  }

  private mapErrorToMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 0) {
        return 'Keine Verbindung zum Server';
      }
      if (err.status === 400) {
        const backendMsg =
          (err.error && (err.error.message || err.error.detail)) ||
          (typeof err.error === 'string' ? err.error : '');
        return backendMsg || 'Ungültige Anfrage';
      }
      if (err.status === 401 || err.status === 403) {
        return 'Sitzung abgelaufen. Bitte neu anmelden';
      }
      if (err.status === 404) {
        let backendMsg = '';
        if (err.error) {
          if (typeof err.error === 'string') {
            try {
              const errorObj = JSON.parse(err.error);
              backendMsg = errorObj.message || errorObj.detail || '';
            } catch (e) {
              backendMsg = err.error;
            }
          } else if (typeof err.error === 'object') {
            backendMsg = err.error.message || err.error.detail || '';
          }
        }
        return backendMsg || 'Antrag nicht gefunden';
      }
      if (err.status === 409) {
        const backendMsg =
          (err.error && (err.error.message || err.error.detail)) ||
          (typeof err.error === 'string' ? err.error : '');
        return backendMsg || 'Konflikt: Diese Operation ist im aktuellen Status nicht erlaubt';
      }
      if (err.status === 413) {
        return 'Datei zu groß. Maximale Größe: 500MB';
      }
      if (err.status >= 500) {
        return 'Serverfehler. Bitte später erneut versuchen';
      }
    }
    return 'Unerwarteter Fehler. Bitte später erneut versuchen';
  }
}
